const SECTION_ID = 'pseudo-variant-item';
const SEARCH_LIMIT = 25;

class PseudoVariantsComponent extends HTMLElement {
  connectedCallback() {
    this.#fetchVariants();
  }

  async #fetchVariants() {
    const title = this.dataset.productTitle;
    const handle = this.dataset.productHandle;
    const container = this.querySelector('[data-pseudo-variants-container]');

    if (!handle || !container) return;

    const stitchedHandles = this.dataset.stitchedHandles
      ? this.dataset.stitchedHandles.split(',').filter(Boolean)
      : [];

    const handles = new Set(stitchedHandles);

    if (title) {
      const matches = await searchMatchingProducts(title, handle);

      for (const match of matches) {
        handles.add(match.handle);
      }
    }

    if (handles.size === 0) return;

    this.hidden = false;

    const results = await Promise.all(
      [...handles].map((h) => fetchVariantHTML(h))
    );

    for (const html of results) {
      if (!html) continue;
      container.insertAdjacentHTML('beforeend', html);
    }
  }
}

/**
 * Searches for products with the same title but a different handle.
 *
 * @param {string} title - The product title to search for.
 * @param {string} currentHandle - The current product handle to exclude.
 * @returns {Promise<Array<{handle: string, title: string}>>} Matching products.
 */
async function searchMatchingProducts(title, currentHandle) {
  const params = new URLSearchParams({
    q: title,
    'resources[type]': 'product',
    'resources[limit]': String(SEARCH_LIMIT),
  });

  const response = await fetch(`/search/suggest.json?${params}`);

  if (!response.ok) return [];

  const data = await response.json();
  const products = data.resources?.results?.products ?? [];

  return products.filter(
    (p) => p.title === title && p.handle !== currentHandle
  );
}

/**
 * Fetches the rendered HTML for a pseudo-variant item using the Section Rendering API.
 *
 * @param {string} handle - The product handle to fetch.
 * @returns {Promise<string|null>} The inner HTML of the rendered section, or null on failure.
 */
async function fetchVariantHTML(handle) {
  const response = await fetch(
    `/products/${handle}?section_id=${SECTION_ID}`
  );

  if (!response.ok) return null;

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const section = doc.querySelector(`[id^="shopify-section"]`);

  return section?.innerHTML?.trim() || null;
}

if (!customElements.get('pseudo-variants-component')) {
  customElements.define('pseudo-variants-component', PseudoVariantsComponent);
}
